import type { Deliverable } from './CreateProjectContext';
import { useSortable } from '@dnd-kit/react/sortable';
import { Card, CardTitle, CardAction, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  Trash2,
  Copy,
  Timer,
  Proportions,
  Hd,
  Play,
  GripVertical,
  Video,
} from 'lucide-react';
import { ButtonGroup } from '@/components/ui/button-group';
import EditDeliverableDialog from './EditDeliverableDialog';
import { Input } from '@/components/ui/input';

interface DeliverableCardProps {
  index: number;
  deliverable: Deliverable;
  className?: string;
  onUpdate: (index: number, updated: Partial<Deliverable>) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
}

export default function DeliverableCard({
  index,
  deliverable,
  className,
  onDelete,
  onUpdate,
  onDuplicate,
}: DeliverableCardProps) {
  const { ref, handleRef, isDragging } = useSortable({
    id: deliverable.id,
    index,
  });

  return (
    <Card
      ref={ref}
      className={`${className} ${isDragging ? 'relative z-50 opacity-75' : ''}`}
    >
      <div className="flex flex-row items-center justify-between px-6">
        <div className="min-w-0 flex-1 pr-4">
          <CardTitle className="text-xl">
            <div className="relative inline-grid min-w-[10ch] max-w-full">
              <span
                className="invisible whitespace-pre border border-transparent text-xl! font-semibold px-2 col-start-1 row-start-1"
                aria-hidden
              >
                {deliverable.deliverableName || ' '}
              </span>
              <Input
                id={`deliverable-name-${index}`}
                value={deliverable.deliverableName}
                autoComplete="off"
                onChange={(e) =>
                  onUpdate(index, { deliverableName: e.target.value })
                }
                className="border border-transparent bg-transparent! shadow-none text-xl! font-semibold px-2 w-full col-start-1 row-start-1 focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-border rounded"
              />
            </div>
          </CardTitle>
        </div>
        <CardAction className="flex flex-row items-center gap-2 self-stretch -mr-3">
          <ButtonGroup>
            <Tooltip>
              <TooltipTrigger asChild>
                <EditDeliverableDialog
                  deliverable={deliverable}
                  onUpdate={(updated) => onUpdate(index, updated)}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="cursor-pointer"
                  variant={'outline'}
                  size={'sm'}
                  onClick={() => onDuplicate(index)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Duplicate</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="cursor-pointer"
                  variant={'destructive'}
                  size={'sm'}
                  onClick={() => onDelete(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete</p>
              </TooltipContent>
            </Tooltip>
          </ButtonGroup>
          <Button
            ref={handleRef}
            variant={'ghost'}
            size={'icon'}
            className="ml-2"
          >
            <GripVertical className="w-5 h-5" />
          </Button>
        </CardAction>
      </div>
      <Separator />
      <CardContent>
        <div className="flex flex-row gap-8 w-full text-xs">
          <Tooltip>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Video className="w-4 h-4" />
              </span>
              <span>{deliverable.videoType}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Video Type</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Timer className="w-4 h-4" />
              </span>
              <span>{deliverable.lengthRange}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Length Range</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Proportions className="w-4 h-4" />
              </span>
              <span>{deliverable.aspectRatio}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Aspect Ratio</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Hd className="w-4 h-4" />
              </span>
              <span>{deliverable.resolution}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resolution</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Play className="w-4 h-4" />
              </span>
              <span>{deliverable.frameRate}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Frame Rate</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
}
